import { useRouter } from '@bprogress/next';
import Chat from '@c/Chat';
import ChatModal from '@c/modals/ChatModal';
import type { ChatDocument } from '@models/chat';
import type { OrderDocument } from '@models/order';
import { markAsRead } from '@util/firestore/messages';
import useRealtimeChats from '@util/hooks/useRealtimeChats';
import { ADMIN_UID, SUPPORT_UIDS } from '@util/support';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { useAuth } from './AuthContext';

type ChatProviderProps = {
  children: ReactNode;
};
type ChatContext = {
  chatOpen: boolean;
  openChatDrawer: (chat: ChatDocument) => void;
  setChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chatModalOpen: boolean;
  setChatModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openChatModalWithRecipientAndProductId: ({
    recipientUid,
    productId,
    initialMessage,
  }: {
    recipientUid: string;
    productId?: string;
    initialMessage?: string;
  }) => void;
  openChatModalWithOrder: (order: OrderDocument, seller_id?: string) => void;
  getChatCount: () => number;
  getUnreadCount: () => number;
  chats: ChatDocument[] | undefined;
  offerChats: ChatDocument[] | undefined;
  isLoading: boolean;
  setSearchTerm: (term: string) => void;
  currentSearchTerm: string;
  setSelectedChat: (chat?: ChatDocument) => void;
};
const ChatContext = createContext({} as ChatContext);

export function useChat() {
  return useContext(ChatContext);
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [chatOpen, setChatOpen] = useState(false);
  const { userDoc, user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');

  const { realtimeChats: chats } = useRealtimeChats(userDoc);
  const { realtimeChats: offerChats } = useRealtimeChats(userDoc, 'offers');

  const setSearchTerm = useCallback((term: string) => {
    setCurrentSearchTerm(term);
  }, []);

  function getChatCount() {
    return chats?.length ?? 0;
  }

  function getUnreadCount() {
    if (!user?.uid || !chats) return 0;
    if (userDoc?.roles?.support) {
      return chats.reduce(
        (acc, chat) =>
          !chat.is_offer &&
          (user.uid !== ADMIN_UID || !chat.is_auto) &&
          Object.keys(SUPPORT_UIDS).some((id) => chat.unread?.[id])
            ? acc + 1
            : acc,
        0
      );
    }
    return chats.reduce(
      (acc, chat) =>
        !chat.is_offer &&
        (user.uid !== ADMIN_UID || !chat.is_auto) &&
        chat.unread?.[user.uid]
          ? acc + 1
          : acc,
      0
    );
  }

  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [recipientUid, setRecipientUid] = useState('');
  const [order, setOrder] = useState<OrderDocument | undefined>(undefined);
  const [productId, setProductId] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  function openChatModalWithRecipientAndProductId({
    recipientUid,
    productId,
    initialMessage,
  }: {
    recipientUid: string;
    productId?: string;
    initialMessage?: string;
  }): void {
    if (user) {
      // if (user.uid !== recipientUid) {
      setRecipientUid(recipientUid);
      if (productId) setProductId(productId);
      setOrder(undefined);
      setInitialMessage(initialMessage ?? '');
      setChatModalOpen(true);
      // }
    } else router.push(`/login?redirect=${encodeURI(pathname ?? '/')}`);
  }

  function openChatModalWithOrder(
    order: OrderDocument,
    recipientUid?: string
  ): void {
    if (user) {
      if (recipientUid) {
        setRecipientUid(recipientUid);
        setOrder(order);
        setProductId('');
        setChatModalOpen(true);
      } else if (user.uid !== order.buyer_id) {
        setRecipientUid(order.buyer_id);
        setOrder(order);
        setProductId('');
        setChatModalOpen(true);
      }
    } else {
      router.push(`/login?redirect=${encodeURI(pathname ?? '/')}`);
    }
  }

  const [selectedChat, setSelectedChat] = useState<ChatDocument | undefined>();

  function openChatDrawer(chat: ChatDocument) {
    if (chat?.id && user?.uid) markAsRead(chat, user.uid);
    setSelectedChat(chat);
    setChatOpen(true);
  }

  return (
    <ChatContext.Provider
      value={{
        chatOpen,
        setChatOpen,
        openChatDrawer,
        chatModalOpen,
        setChatModalOpen,
        openChatModalWithRecipientAndProductId,
        openChatModalWithOrder,
        getChatCount,
        getUnreadCount,
        chats,
        offerChats,
        isLoading: false,
        setSearchTerm,
        currentSearchTerm,
        setSelectedChat,
      }}
    >
      <Chat chat={selectedChat} setSelectedChat={setSelectedChat} />
      <ChatModal
        key={initialMessage + recipientUid + productId + order?.id}
        isOpen={chatModalOpen}
        dismiss={() => setChatModalOpen(false)}
        recipientUid={recipientUid}
        productId={productId}
        order={order}
        initialMessage={initialMessage}
      />
      {children}
    </ChatContext.Provider>
  );
}
